import React, { useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { LogEntry, LogEntryType } from '@/parsing/LogEntry';
import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

interface Props {
  currentEntryIndex: number;
  currentEntryLabel: string;
  onRequestSurroundingEntries: (
    entryIndex: number,
    direction: 'before' | 'after',
    count: number,
  ) => LogEntry[];
}

const INITIAL_COUNT = 20;
const LOAD_MORE_INCREMENT = 20;

function getEntryLabel(entry: LogEntry): {
  text: string;
  color: string;
  bg: string;
  border: string;
} {
  switch (entry.entryType) {
    case LogEntryType.NetworkRequest: {
      const outcome = entry.extra.networkRequest?.outcome;
      switch (outcome) {
        case NetworkRequestOutcome.Failed:
          return { text: 'Failed Request', color: '#c0392b', bg: '#fdecea', border: '#f1998e' };
        case NetworkRequestOutcome.ProbablyFailed:
          return {
            text: 'Probably Failed Request',
            color: '#6a1b9a',
            bg: '#f3e5f5',
            border: '#ce93d8',
          };
        case NetworkRequestOutcome.Canceled:
          return { text: 'Canceled Request', color: '#b07a00', bg: '#fff8e1', border: '#ffe082' };
        case NetworkRequestOutcome.Succeeded:
          return { text: 'Successful Request', color: '#1b5e20', bg: '#e8f5e9', border: '#a5d6a7' };
        default:
          return { text: 'Request', color: '#424242', bg: '#f5f5f5', border: '#bdbdbd' };
      }
    }
    case LogEntryType.NetworkResponse: {
      const outcome = entry.extra.networkRequest?.outcome;
      switch (outcome) {
        case NetworkRequestOutcome.Failed:
          return { text: 'Failed Response', color: '#c0392b', bg: '#fdecea', border: '#f1998e' };
        case NetworkRequestOutcome.ProbablyFailed:
          return {
            text: 'Probably Failed Response',
            color: '#6a1b9a',
            bg: '#f3e5f5',
            border: '#ce93d8',
          };
        case NetworkRequestOutcome.Canceled:
          return { text: 'Canceled Response', color: '#b07a00', bg: '#fff8e1', border: '#ffe082' };
        case NetworkRequestOutcome.Succeeded:
          return {
            text: 'Successful Response',
            color: '#1b5e20',
            bg: '#e8f5e9',
            border: '#a5d6a7',
          };
        default:
          return { text: 'Response', color: '#424242', bg: '#f5f5f5', border: '#bdbdbd' };
      }
    }
    case LogEntryType.MQTTMessage:
      return { text: 'MQTT', color: '#01579b', bg: '#e1f5fe', border: '#81d4fa' };
    case LogEntryType.ReduxAction:
      return { text: 'Redux', color: '#4527a0', bg: '#ede7f6', border: '#b39ddb' };
    default:
      return { text: 'Event', color: '#37474f', bg: '#eceff1', border: '#b0bec5' };
  }
}

function parseAdditionalInfo(additionalInfo: string): string {
  const domainMatch = additionalInfo.match(/Domain:\s*([^,]+)/);
  const codeMatch = additionalInfo.match(/Code:\s*(-?\d+)/);
  const descMatch = additionalInfo.match(/"NSLocalizedDescription":\s*([^,"[\]]+)/);

  const parts: string[] = [];
  if (domainMatch) {
    parts.push(domainMatch[1].trim());
  }
  if (codeMatch) {
    parts.push(`Code ${codeMatch[1].trim()}`);
  }
  if (descMatch) {
    parts.push(descMatch[1].trim());
  }

  return parts.length > 0 ? parts.join(' · ') : additionalInfo.slice(0, 80);
}

function getNetworkPreview(entry: LogEntry): string[] {
  const lines: string[] = [];
  const net = entry.extra.networkRequest;
  if (!net) {
    return [entry.content?.slice(0, 180) ?? ''];
  }

  const path = net.url?.pathname ?? '';
  if (path) {
    lines.push(path);
  }

  const httpCode = net.response?.httpCode;
  const duration = net.response?.durationSec;
  const codeDuration: string[] = [];
  if (httpCode) {
    codeDuration.push(`HTTP ${httpCode}`);
  }
  if (duration != null) {
    codeDuration.push(`${duration.toFixed(3)}s`);
  }
  if (codeDuration.length > 0) {
    lines.push(codeDuration.join('  '));
  }

  const bodyJson = net.response?.bodyJson;
  if (bodyJson?.code != null && bodyJson.code !== 0) {
    const msg = bodyJson.message ? `${bodyJson.code} · ${bodyJson.message}` : `${bodyJson.code}`;
    lines.push(msg);
  }

  const additionalInfo = net.response?.additionalInfo;
  if (additionalInfo) {
    lines.push(parseAdditionalInfo(additionalInfo));
  }

  return lines.length > 0 ? lines : [entry.content?.slice(0, 180) ?? ''];
}

function getEntryPreview(entry: LogEntry): string[] {
  if (
    entry.entryType === LogEntryType.NetworkRequest ||
    entry.entryType === LogEntryType.NetworkResponse
  ) {
    return getNetworkPreview(entry);
  }
  const content = entry.content ?? '';
  return [content.length > 180 ? `${content.slice(0, 180)}…` : content];
}

function EntryRow({ entry }: { entry: LogEntry }) {
  const label = getEntryLabel(entry);
  const previewLines = getEntryPreview(entry);
  const time = entry.date ? DateTime.fromJSDate(entry.date).toFormat('HH:mm:ss.SSS') : '';
  const module = entry.extra.generic?.module;

  return (
    <Box
      sx={{
        px: 2,
        py: 0.75,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.015)' },
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(0,0,0,0.4)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}
        >
          #{entry.index + 1}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(0,0,0,0.5)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}
        >
          {time}
        </Typography>
        {module && (
          <Typography
            variant="caption"
            sx={{ color: 'rgba(0,0,0,0.4)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          >
            🧱 {module}
          </Typography>
        )}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 0.75,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: label.bg,
            border: `1px solid ${label.border}`,
            lineHeight: 1,
          }}
        >
          <Typography
            component="span"
            sx={{
              color: label.color,
              fontWeight: 600,
              fontSize: '0.65rem',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              fontFamily: 'var(--font-family)',
            }}
          >
            {label.text}
          </Typography>
        </Box>
      </Stack>

      {previewLines.map((line, i) => (
        <Typography
          key={i}
          variant="caption"
          sx={{
            display: 'block',
            fontFamily: 'monospace',
            fontSize: '0.72rem',
            color: i === 0 ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.55)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            lineHeight: 1.4,
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
}

function LoadMoreButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 1,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        backgroundColor: 'rgba(0,0,0,0.02)',
      }}
    >
      <Button
        size="small"
        variant="text"
        onClick={onClick}
        sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)' }}
      >
        {label}
      </Button>
    </Box>
  );
}

export function ContextualEvents({
  currentEntryIndex,
  currentEntryLabel,
  onRequestSurroundingEntries,
}: Props) {
  const [open, setOpen] = useState<'before' | 'after' | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleOpen = (direction: 'before' | 'after') => {
    setVisibleCount(INITIAL_COUNT);
    setOpen(direction);
  };

  const handleClose = () => {
    setOpen(null);
    setVisibleCount(INITIAL_COUNT);
  };

  const entries =
    open !== null ? onRequestSurroundingEntries(currentEntryIndex, open, visibleCount) : [];

  // Whether there might be more entries beyond what's currently shown
  const hasMore = entries.length === visibleCount;

  const loadMore = () => {
    if (open === 'before' && scrollRef.current) {
      // Snapshot scroll state before React adds new entries at the top
      const el = scrollRef.current;
      const scrollHeightBefore = el.scrollHeight;
      const scrollTopBefore = el.scrollTop;

      setVisibleCount((c) => c + LOAD_MORE_INCREMENT);

      // After re-render, push scrollTop down by however much height was added
      // so the user's viewport stays exactly where it was
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = scrollTopBefore + (el.scrollHeight - scrollHeightBefore);
        });
      });
    } else {
      setVisibleCount((c) => c + LOAD_MORE_INCREMENT);
    }
  };

  return (
    <>
      <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
        <Chip
          size="small"
          icon={<ExpandLess fontSize="small" />}
          label={`${INITIAL_COUNT} events before`}
          onClick={() => handleOpen('before')}
          variant="outlined"
          sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
        />
        <Chip
          size="small"
          icon={<ExpandMore fontSize="small" />}
          label={`${INITIAL_COUNT} events after`}
          onClick={() => handleOpen('after')}
          variant="outlined"
          sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
        />
      </Box>

      <Dialog
        open={open !== null}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '75vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              {open === 'before' ? '↑ events before' : '↓ events after'}
              <span style={{ fontWeight: 400, color: 'rgba(0,0,0,0.5)', marginLeft: 8 }}>
                {currentEntryLabel}
              </span>
            </Typography>
            <Button size="small" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent ref={scrollRef} sx={{ p: 0 }}>
          {/* "Load more" at TOP for "before" direction */}
          {open === 'before' && hasMore && (
            <LoadMoreButton label={`↑ Load ${LOAD_MORE_INCREMENT} more`} onClick={loadMore} />
          )}

          {/* For "before": entries oldest→newest, then current anchor at bottom */}
          {open === 'before' && (
            <>
              {entries.map((entry) => (
                <EntryRow key={entry.index} entry={entry} />
              ))}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: '#fff8e6',
                  borderLeft: '4px solid #f0b429',
                  borderTop: '1px solid #f0d080',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#b07a00', fontFamily: 'monospace' }}
                >
                  ◆ {currentEntryLabel} — current entry
                </Typography>
              </Box>
            </>
          )}

          {/* For "after": current anchor at top, then entries */}
          {open === 'after' && (
            <>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: '#fff8e6',
                  borderLeft: '4px solid #f0b429',
                  borderBottom: '1px solid #f0d080',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#b07a00', fontFamily: 'monospace' }}
                >
                  ◆ {currentEntryLabel} — current entry
                </Typography>
              </Box>
              {entries.map((entry) => (
                <EntryRow key={entry.index} entry={entry} />
              ))}
            </>
          )}

          {/* "Load more" at BOTTOM for "after" direction */}
          {open === 'after' && hasMore && (
            <LoadMoreButton label={`↓ Load ${LOAD_MORE_INCREMENT} more`} onClick={loadMore} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
